import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { User } from "lucide-react";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

/* Dark-theme partner typeahead (for /ai + /bewerbung). Same behavior as
   PartnerAutocomplete but styled for dark fintech pages. */
export default function DarkPartnerAutocomplete({ value, onChange, placeholder, required, testid }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 2) {
      setItems([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/partners/search`, { params: { q: value } });
        setItems(res.data || []);
        setOpen((res.data || []).length > 0);
      } catch {
        setItems([]);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <input
        data-testid={testid || "dark-partner-autocomplete"}
        type="text"
        value={value}
        required={required}
        onChange={(e) => {
          onChange(e.target.value, null);
          if (e.target.value.length >= 2) setOpen(true);
        }}
        onFocus={() => value.length >= 2 && items.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3.5 text-white placeholder-slate-400 outline-none focus:border-violet-400"
      />
      {open && items.length > 0 && (
        <ul
          data-testid="dark-partner-suggestions"
          className="absolute z-30 left-0 right-0 mt-1.5 rounded-xl border border-white/15 bg-[#1a163d] shadow-2xl max-h-60 overflow-y-auto"
        >
          {items.map((p) => (
            <li
              key={p.id}
              data-testid={`dark-partner-suggestion-${p.id}`}
              onMouseDown={() => { onChange(p.name, p.id); setOpen(false); }}
              className="px-4 py-2.5 text-sm cursor-pointer hover:bg-violet-500/20 transition-colors flex items-center gap-2 text-slate-200"
            >
              <User className="h-3.5 w-3.5 text-violet-400" />
              <span className="font-medium">{p.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
